import {
  JobSpecimenLineItem,
  JobServiceLineItem,
  JobLineItem,
} from "../@types/job";

export function isSpecimenLineItem(
  item: JobSpecimenLineItem | JobServiceLineItem
): item is JobSpecimenLineItem {
  return "specimen" in item;
}

export function isServiceLineItem(
  item: JobSpecimenLineItem | JobServiceLineItem
): item is JobServiceLineItem {
  return "service" in item;
}

// Identify processing-fee service line items. The service NAME is unreliable
// (e.g. "Online Domestic Card for Deposit Payment" contains no "Processing Fee"
// text), so we also check the notes marker stamped on creation
// (`type: "ON_PAYMENT"` — see processJobPayment.ts).
export function isProcessingFeeLineItem(item: JobLineItem): boolean {
  if (!isServiceLineItem(item)) return false;
  if (item.service?.toLowerCase().includes("processing fee")) return true;
  try {
    const notes = JSON.parse(item.notes);
    return notes?.type === "ON_PAYMENT";
  } catch {
    return false;
  }
}

// Whether a processing-fee line should be included in the taxable base.
// The taxable flag is stamped into notes alongside the ON_PAYMENT marker when
// the fee is created. Parsing is guarded because a hand-created service line
// can carry free text here, and an unguarded JSON.parse would take the whole
// tax calculation down with it.
export function isTaxableProcessingFeeLineItem(item: JobLineItem): boolean {
  if (!isProcessingFeeLineItem(item)) return false;
  try {
    return JSON.parse((item as JobServiceLineItem).notes)?.taxable === true;
  } catch {
    return false;
  }
}

// Hide $0 "Client Not Interested" line items from invoices only.
// Trophy list / job card still show them — they exist physically and are tracked as inventory.
export function isClientNotInterested(item: JobLineItem): boolean {
  const hasTag = item.tags?.some(
    (t) => t.trim().toLowerCase() === "client is not interested"
  );
  return Boolean(hasTag) && !item.price;
}

// Resolve the mount a service is linked to. Returns null for unlinked services,
// specimen items, and dangling links (the mount was deleted).
export function getLinkedMount(
  item: JobLineItem,
  lineItems: JobLineItem[]
): JobSpecimenLineItem | null {
  if (!isServiceLineItem(item) || !item.linkedSpecimenUid) return null;
  return (
    lineItems
      .filter(isSpecimenLineItem)
      .find((mount) => mount.uid === item.linkedSpecimenUid) || null
  );
}

export function mountLinkLabel(mount: JobSpecimenLineItem): string {
  const name = mount.specimen.species || "New Mount";
  return mount.frontFacingId ? `${name} • ${mount.frontFacingId}` : name;
}

// Keep each linked service directly after its mount, and processing fees at
// the very end (the drag clamp in the job forms assumes fees are last, and a
// mid-list fee blocks every downward drag above it). Mounts and unlinked
// services keep their relative order; a dangling link is treated as unlinked.
export function orderLineItemsWithLinks(
  lineItems: JobLineItem[]
): JobLineItem[] {
  const ordered: JobLineItem[] = [];
  const fees: JobLineItem[] = [];
  lineItems.forEach((item) => {
    if (isProcessingFeeLineItem(item)) {
      fees.push(item);
      return;
    }
    if (getLinkedMount(item, lineItems)) return;
    ordered.push(item);
    if (isSpecimenLineItem(item)) {
      lineItems.forEach((candidate) => {
        if (
          isServiceLineItem(candidate) &&
          !isProcessingFeeLineItem(candidate) &&
          candidate.linkedSpecimenUid === item.uid
        ) {
          ordered.push(candidate);
        }
      });
    }
  });
  return [...ordered, ...fees];
}

// Each group is one drag target: a mount plus all of its linked services,
// or a standalone service/fee. Keep the original objects for form bindings.
export function groupLineItemsWithLinks(lineItems: JobLineItem[]): JobLineItem[][] {
  const groups: JobLineItem[][] = [];
  for (const item of orderLineItemsWithLinks(lineItems)) {
    const mount = !isProcessingFeeLineItem(item) && getLinkedMount(item, lineItems);
    const group = mount && groups.find((items) => items[0].uid === mount.uid);
    if (group) group.push(item);
    else groups.push([item]);
  }
  return groups;
}

export function moveLineItemGroup(
  lineItems: JobLineItem[],
  sourceIndex: number,
  destinationIndex: number
): JobLineItem[] {
  const groups = groupLineItemsWithLinks(lineItems);
  if (!groups[sourceIndex] || isProcessingFeeLineItem(groups[sourceIndex][0])) {
    return lineItems;
  }
  const firstFee = groups.findIndex((group) => isProcessingFeeLineItem(group[0]));
  const lastMovable = firstFee === -1 ? groups.length - 1 : firstFee - 1;
  const target = Math.max(0, Math.min(destinationIndex, lastMovable));
  const [moved] = groups.splice(sourceIndex, 1);
  groups.splice(target, 0, moved);
  return groups.flat();
}

export function generateFrontFacingId(
  existingLineItems: JobLineItem[],
  skipGeneration: boolean = false
): string {
  if (skipGeneration) {
    return "";
  }

  const specimenLineItems = existingLineItems.filter(isSpecimenLineItem);

  if (specimenLineItems.length === 0) {
    return "M1";
  }

  // Extract numeric indices from existing frontFacingIds
  const existingIndices = specimenLineItems
    .map((item) => item.frontFacingId)
    .filter((id) => id && (id.startsWith("m") || id.startsWith("M")))
    .map((id) => parseInt(id.slice(1), 10))
    .filter((num) => !isNaN(num));

  if (existingIndices.length === 0) {
    return "M1";
  }

  // Find the highest existing index and increment by 1
  const maxIndex = Math.max(...existingIndices);
  return `M${maxIndex + 1}`;
}

export function isValidFrontFacingId(
  frontFacingId: string,
  existingLineItems: JobLineItem[],
  currentItemUid?: string
): boolean {
  if (!frontFacingId || frontFacingId.trim().length === 0) {
    return false;
  }

  const specimenLineItems = existingLineItems.filter(isSpecimenLineItem);

  // Check for duplicates, excluding the current item being edited
  const isDuplicate = specimenLineItems.some(
    (item) =>
      item.frontFacingId?.toLowerCase() === frontFacingId.toLowerCase() &&
      item.uid !== currentItemUid
  );

  return !isDuplicate;
}
